import { MutationTree, ActionTree, ActionContext } from 'vuex';
import { of } from 'rxjs';
import { map, filter, mergeMap, finalize } from 'rxjs/operators';
import { Payment, User } from '@/types';
import { PaymentService } from 'api';
import { Events } from '@/analytics';
import { guid } from './support';

export class State {
  card: Payment.Card = new Payment.Card();
  loading: boolean = false;
  error: string = '';
  isAddCardModalShown: boolean = false;
  selectedCardIndex: number = 0;
  filledCard: User.Card = null;
}

const mutations = <MutationTree<State>>{
  setCard(state: State, card: Payment.Card) {
    state.card = card;
  },
  setCardNumber(state: State, cardNumber: string) {
    state.card.number.value = cardNumber.split(' ').join('');
  },
  setMM(state: State, mm: string) {
    state.card.expiration.mm.value = mm.split(' ').join('');
  },
  setYY(state: State, yy: string) {
    state.card.expiration.yy.value = yy.split(' ').join('');
  },
  setCardholder(state: State, cardholder: string) {
    state.card.cardholder.value = cardholder;
  },
  setLoading(state: State, loading: boolean) {
    state.loading = loading;
  },
  setError(state: State, error: string) {
    state.error = error;
  },
  toggleAddCardModal(state: State, show: boolean) {
    state.isAddCardModalShown = show;
  },
  setSubmitted(state: State) {
    state.card.isSubmitted = true;
  },
  resetStore(state: State) {
    state.card = new Payment.Card();
    state.loading = false;
    state.error = '';
    state.isAddCardModalShown = false;
  },

  setSelectedCardIndex(state: State, index: number) {
    state.selectedCardIndex = index;
  },
  setFilledCard(state: State, card: User.Card) {
    state.filledCard = card;
  },
};

const actions = <ActionTree<State, any>>{
  showAddCard(
    store: ActionContext<State, any>,
    {
      show,
      card,
    }: {
      show: boolean;
      card?: Payment.Card;
    },
  ) {
    store.commit('setError', '');

    if (show) {
      const _card = new Payment.Card(card);
      if (!card) {
        const user: User.UserV2 | null = store.rootState.user?.userV2;
        _card.cardholder.value = [user?.firstName, user?.lastName]
          .filter(it => !!it)
          .join(' ');
        _card.number.value = user?.bin ?? '';
      }
      store.commit('setCard', _card);

      Events.Payment.AddCard.start();
    }

    store.commit('toggleAddCardModal', show);
  },

  setCardNumber(store: ActionContext<State, any>, cardNumber: string) {
    store.commit('setCardNumber', cardNumber);
  },

  addCardV2(store: ActionContext<State, any>) {
    store.commit('setSubmitted');

    if (store.state.card.isValid()) {
      store.commit('setLoading', true);

      const user: User.UserV2 | null = store.rootState.user?.userV2;

      if (!user) {
        return;
      }

      store.state.card.saveCard(user.userId);

      store.commit(
        'setFilledCard',
        store.state.card.temporaryToUserCard(guid()),
      );
      // store.dispatch('user/updateCardsList', {}, { root: true });
      store.commit('toggleAddCardModal', false);
      store.commit('setLoading', false);
    }
  },

  removeCard(store: ActionContext<State, any>, cardId: string) {
    store.commit('setLoading', true);
    PaymentService.removeCardUseCase({ card_id: cardId })
      .pipe(
        finalize(() => {
          store.commit('setLoading', false);
        }),
      )
      .subscribe(
        res => {
          store.dispatch('user/updateUserCards', res.user.payment_cards, {
            root: true,
          });
        },
        err => {
          store.dispatch('booking/showFailure', err, { root: true });
        },
      );
  },

  selectCard(store: ActionContext<State, any>, indexOfCard: number) {
    store.commit('setSelectedCardIndex', indexOfCard);
  },

  onDestroy(store: ActionContext<State, any>) {
    store.commit('resetStore');
  },
};

export const card = {
  namespaced: true,
  state: new State(),
  // getters,
  mutations,
  actions,
};

export default {};
